/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { LinearProgress } from '@material-ui/core';
import {
  REAUTHORIZATION_STATUS,
  SESSION_STORAGE_KEYS,
  TRACKING,
  SUPPORTED_CHAT_TOOLS,
  TEAM_TYPES
} from 'common/constants/constants';
import { SPACES } from 'common/constants/sizes';
import language from 'common/language/en.lang';
import * as ZendeskService from 'common/services/zendesk.service';
import { updateTeamSettings } from 'common/services/settings.service';
import {
  handlePostSlackAuth,
  redirectToSlack
} from 'common/services/slack.service';
import * as TrackingService from 'common/services/tracking.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { omitFromQs } from 'common/utils/utils';
import { WhiteSection } from 'components/base/layout-components';
import Table from 'components/base/Table';
import Text from 'components/base/Text';
import slackLogoColored from 'images/logos/slack-logo.svg';
import {
  createNewSlackConnectionModalOption,
  createSelectTeamSlackChannelModalOptions
} from 'modals/modal-options-factory';
import QueryString from 'query-string';
import styled from 'styled-components';
import GeneralTopHeaders from './components/GeneralTopHeaders';
import SlackConnectedTopBar from './components/SlackConnectedTopBar';
import TeamTopHeaders from './components/TeamTopHeaders';
import {
  getTableColumns,
  getTableData,
  createTeamData,
  loadTeamsSettings,
  TableWrapper
} from '../ChatTools/common';
import { toggleNotificationsForTeam } from '../ChatTools/notifications.service';
import NotConnected from '../ChatTools/NotConnected';
import ActionableNotifications from './components/ActionableNotifications';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import useCollaborationSettings from 'components/hooks/useCollaborationSettings/useCollaborationSettings';
import { isTeamLicenseSelector } from 'state/selectors/license';
import { LBButton } from '@linearb/buzz';

const Loader = styled(LinearProgress)`
  margin: 2rem;
`;

const { successNotificationExistingSlackAccount } =
  language.Setting.Slack.reauthorize;

export const SHOW_SLACK_NOTIFICATION_PARAM = 'showSlackNotification';

const ConnectButtonSVG = styled.img`
  ${({ size }) => `width: ${size === 'medium' ? '1.2rem' : '1rem'}`};
  ${({ size }) => `height: ${size === 'medium' ? '1.2rem' : '1rem'}`};
`;

export const getConnectToSlackButton =
  (
    slackSensorAuthorization = null,
    openSlackChannelModal = null,
    settings = null
  ) =>
  (team, size = 'default') => {
    const handleSlackButtonClicked = () => {
      slackSensorAuthorization && openSlackChannelModal
        ? openSlackChannelModal(team, settings[team.id], true)
        : redirectToSlack(team);
    };

    return (
      <LBButton size={size} onClick={handleSlackButtonClicked}>
        {size !== 'sm' && (
          <ConnectButtonSVG
            src={slackLogoColored}
            alt={SUPPORTED_CHAT_TOOLS.SLACK}
            size='medium'
          />
        )}
        {size === 'default'
          ? 'Connect Slack'
          : slackSensorAuthorization
          ? 'Select channel'
          : 'Connect Slack'}
      </LBButton>
    );
  };

// eslint-disable-next-line max-lines-per-function
export default function SlackSettings({
  toggleModal,
  toggleSnackBar,
  currentTeams,
  slackSensorAuthorization,
  location,
  account,
  patchAccount,
  uiState,
  isOrgSettings,
  history,
  selectTeamId,
  isAllowedToConfigureWorkerBInlineApproval
}) {
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const [settings, setSettings] = useState(null);
  const { getSettingOrDefinitionValue, updateSetting, loadSettings } =
    useSettingsService();

  const slackOrgSettings = useCollaborationSettings({
    collaborationType: SUPPORTED_CHAT_TOOLS.SLACK
  });

  const workerbInlinePrApproveEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.workerbInlinePrApproveEnabled
  });

  const hasTeams = currentTeams && currentTeams.length;

  const currentTeam =
    !isOrgSettings &&
    currentTeams &&
    currentTeams.find(t => t.id === _.get(uiState, 'selectedTeamId', null));

  const openSlackChannelModal = (team, teamSettings = {}, isEdit = false) => {
    const onSubmit = async ({ name, id }) => {
      await updateTeamSettings(team.id, { teamSlackChannel: { name, id } });
      await loadSettings();
      setSettings({
        ...settings,
        [team.id]: {
          ...teamSettings,
          slackChannel: { name, id },
          isNotificationEnabled: teamSettings.slackChannel
            ? teamSettings.isNotificationEnabled
            : true
        }
      });
      const showSlackNotification = _.get(
        account,
        `settings.${SHOW_SLACK_NOTIFICATION_PARAM}`,
        null
      );
      if (showSlackNotification !== false) {
        patchAccount(account.id, {
          settings: {
            ...account.settings,
            [SHOW_SLACK_NOTIFICATION_PARAM]: false
          }
        });
        toggleSnackBar({ isOpen: false });
      }
      toggleModal();
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: isOrgSettings
          ? TRACKING.PAGE_TABS.SLACK
          : TRACKING.PAGE_TABS.TEAM_SLACK,
        click_type: isEdit
          ? TRACKING.CLICK.CHANGE_CHANNEL
          : TRACKING.CLICK.CONNECT_TEAM,
        ...(isOrgSettings && { team_name: team.name })
      });
    };

    toggleModal(
      createSelectTeamSlackChannelModalOptions({
        onSubmit,
        onCancel: () => toggleModal(),
        title: isEdit ? (
          'Select Slack channel'
        ) : (
          <span>You successfully connected Slack!</span>
        ),
        preselected: _.get(teamSettings, 'slackChannel', null)
      })
    );
  };

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: isOrgSettings
        ? TRACKING.PAGE_TABS.ORG_SLACK
        : TRACKING.PAGE_TABS.TEAM_SLACK
    });
  }, []);
  const saveSettings = settingsByTeamId => {
    setSettings(settingsByTeamId);
    const sessionStorageAccessor = BrowserStorageAccessor().session;
    const preDirectTeamId = sessionStorageAccessor.get(
      SESSION_STORAGE_KEYS.SLACK_REDIRECT_TEAM_ID
    );
    if (preDirectTeamId) {
      const team = currentTeams.find(
        ({ id }) => id === parseInt(preDirectTeamId)
      );
      if (team && slackSensorAuthorization) {
        openSlackChannelModal(team, settingsByTeamId[team.id]);
      }
      sessionStorageAccessor.remove(
        SESSION_STORAGE_KEYS.SLACK_REDIRECT_TEAM_ID
      );
    }
    handlePostSlackAuth({ toggleModal });
  };
  useEffect(() => {
    if (!hasTeams) {
      setSettings({});
      return;
    }
    if (isOrgSettings) {
      saveSettings(slackOrgSettings);
    } else {
      loadTeamsSettings([currentTeam]).then(saveSettings);
    }
  }, [currentTeams]);

  const toggleNotificationsForTeamWrapper = async (team, value) =>
    await toggleNotificationsForTeam(
      settings,
      setSettings,
      updateSetting,
      isOrgSettings,
      team,
      value,
      _.keys(SUPPORTED_CHAT_TOOLS)[0]
    );

  useEffect(() => {
    if (
      QueryString.parse(location && location.search).oauth_slack === 'failure'
    ) {
      toggleSnackBar({
        message: (
          <Text size='large' color='white'>
            We encountered an issue while trying to connect to slack. Please try
            again
          </Text>
        ),
        variant: 'error'
      });
    }
  }, []);

  useEffect(() => {
    const { reauth_valid, newTeamName, oldTeamName } = QueryString.parse(
      history.location.search
    );

    const openSupportChatAndClose = () => {
      ZendeskService.openSupportRequest();
      toggleModal();
    };

    if (reauth_valid === REAUTHORIZATION_STATUS.VALID) {
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.ORG_SLACK,
        reoauth: 'success',
        workspace: 'same'
      });
      toggleSnackBar({
        message: (
          <Text size='large' color='white'>
            {successNotificationExistingSlackAccount()}
          </Text>
        ),
        variant: 'success'
      });
    } else if (reauth_valid === REAUTHORIZATION_STATUS.NOT_VALID) {
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.ORG_SLACK,
        reoauth: 'failed',
        workspace: 'different'
      });
      toggleModal(
        createNewSlackConnectionModalOption({
          onCancel: () => toggleModal(),
          onSubmit: () => openSupportChatAndClose(),
          newTeamName,
          oldTeamName
        })
      );
    }
    omitFromQs('reauth_valid');
    omitFromQs('stored_authorization_key');
    omitFromQs('newTeamName');
    omitFromQs('oldTeamName');
  }, []);

  const createTeamDataWrapper = (team, teamSettings) => {
    if (!teamSettings) {
      return null;
    }
    const { slackChannel = null } = teamSettings || {};

    return createTeamData(
      team,
      teamSettings,
      slackSensorAuthorization,
      settings,
      selectTeamId,
      history,
      slackChannel,
      slackChannel && `#${slackChannel.name}`,
      true,
      toggleNotificationsForTeamWrapper,
      openSlackChannelModal,
      getConnectToSlackButton(
        slackSensorAuthorization,
        openSlackChannelModal,
        settings
      ),
      null,
      isTeamLicense
    );
  };

  const currentTeamSettings = settings
    ? settings[_.get(currentTeam, 'id', null)]
    : null;

  const teamSlackChannel = _.get(currentTeamSettings, 'slackChannel', null);
  const teamNotificationsEnabled = _.get(
    currentTeamSettings,
    'isNotificationEnabled',
    null
  );

  const isSlackConnected = slackSensorAuthorization;

  const isNotificationsChecked = isSlackConnected
    ? teamNotificationsEnabled
    : false;

  const tableData = getTableData({
    hasTeams,
    isOrgSettings,
    currentTeams: currentTeams.filter(
      ({ type }) => type !== TEAM_TYPES.ALL_CONTRIBUTORS
    ),
    settings,
    currentTeam,
    createTeamData: createTeamDataWrapper,
    uiState
  });
  const tableColumns = getTableColumns('SLACK CHANNEL');

  return (
    <WhiteSection t={SPACES['x-medium']}>
      {!settings && <Loader />}
      {settings && (
        <>
          <SlackConnectedTopBar
            toggleNotificationsForTeam={toggleNotificationsForTeamWrapper}
            currentTeam={currentTeam}
            isOrgSettings={isOrgSettings}
            isSlackConnected={isSlackConnected}
            isNotificationsChecked={isNotificationsChecked}
            sensorAuthorization={slackSensorAuthorization}
            account={account}
            connectToSlack={() =>
              redirectToSlack(currentTeam, slackSensorAuthorization.id, true)
            }
            contactSupport={ZendeskService.openSupportRequest}
            teamSlackChannel={teamSlackChannel}
          />
          {isOrgSettings && isAllowedToConfigureWorkerBInlineApproval && (
            <ActionableNotifications
              isEnabled={workerbInlinePrApproveEnabled}
              onSwitchChange={value =>
                updateSetting(
                  FEATURE_FLAGS_NAMES.workerbInlinePrApproveEnabled,
                  value
                )
              }
            />
          )}
          {isOrgSettings && hasTeams ? (
            <>
              <GeneralTopHeaders settings={settings} />
              <TableWrapper>
                <Table columns={tableColumns} data={tableData} />
              </TableWrapper>
            </>
          ) : !isOrgSettings &&
            slackSensorAuthorization &&
            !!teamSlackChannel ? (
            <>
              <TeamTopHeaders
                currentTeam={currentTeam}
                currentTeamSettings={currentTeamSettings}
                openSlackChannelModal={openSlackChannelModal}
                history={history}
              />
            </>
          ) : (
            <NotConnected
              chatTool={SUPPORTED_CHAT_TOOLS.SLACK}
              isOrgSettings={isOrgSettings}
            >
              {hasTeams &&
                getConnectToSlackButton(
                  slackSensorAuthorization,
                  openSlackChannelModal,
                  settings
                )(currentTeam, 'default')}
            </NotConnected>
          )}
        </>
      )}
    </WhiteSection>
  );
}
